import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminPageLayout from "../../../components/AdminPageLayout/AdminPageLayout";
import { Headings } from "../../../components/Headings";
import LoadingMask from "../../../components/LoadingMask/LoadingMask";
import { useAppAuthState } from "../../../hooks/useAppAuthState";
import { getAllUsers } from "../../../services/userService";
import { User } from "../../../types/User";
import "./AdminUsersPage.scss";

const AdminUsersPage: React.FC = () => {
  const { isUserAdmin, isAuthLoading } = useAppAuthState();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isUserAdmin) {
      setError("Access Denied. You must be an admin to view this page.");
      setIsLoading(false);
      return;
    }

    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const fetchedUsers = await getAllUsers();
        setUsers(fetchedUsers);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching users:", err);
        setError(err.message || "Failed to fetch users.");
      } finally {
        setIsLoading(false);
      }
    };

    if (isUserAdmin) {
      fetchUsers();
    }
  }, [isUserAdmin, isAuthLoading]);

  const handleUserRowClick = useCallback(
    (userId: string) => {
      navigate(`/admin/user/${userId}`);
    },
    [navigate]
  );

  const pageContent = useMemo(() => {
    if (isLoading || isAuthLoading) {
      return null;
    }

    if (error) {
      return <div className="alert alert-danger">{error}</div>;
    }

    if (!isUserAdmin) {
      return (
        <div className="alert alert-danger">
          Access Denied. You must be an admin to view this page.
        </div>
      );
    }

    if (users.length === 0) {
      return <div className="alert alert-info">No users found.</div>;
    }

    return (
      <div className="admin-users-table-container">
        <Link to="/admin" className="btn btn-outline-primary">
          &larr; Admin
        </Link>
        <Headings.Page>User Management</Headings.Page>
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Email</th>
              <th>Created At</th>
              <th>UID</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.uid}
                onClick={() => handleUserRowClick(u.uid)}
                style={{ cursor: "pointer" }}
              >
                <td>{u.email || "N/A"}</td>
                <td>
                  {u.createdAt
                    ? new Date(u.createdAt).toLocaleDateString()
                    : "N/A"}
                </td>
                <td>{u.uid}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [isLoading, isAuthLoading, error, users, isUserAdmin, handleUserRowClick]);

  if (isLoading || isAuthLoading) {
    return <LoadingMask message="Loading users..." />;
  }

  return (
    <AdminPageLayout title="User Management">{pageContent}</AdminPageLayout>
  );
};

export default AdminUsersPage;
